import { CarPaymentCollection } from '@graphql/generated-base';
import type { CarFilters, CarRatesSortDto } from './CarRentalConsole.types';

export const CAR_MIN_REVIEW_COUNT = 5;
export const CAR_SNAPSHOT_READ_LIMIT = 50;

/** Rows mounted per page; full filtered set still drives facet counts / "N results". Keep tall enough that the sentinel leaves the viewport after each append. */
export const CAR_LISTINGS_PAGE_SIZE = 20;

export const CAR_PRICE_RANGE = {
  MIN: 0,
  MAX: 1000,
} as const;

export const DEFAULT_PICKUP_TIME = '10:30';
export const DEFAULT_DROPOFF_TIME = '10:30';

export const DEFAULT_RENTAL_DAYS = 3;

/** A pick-up must start at least this long after now, measured in real minutes at the pick-up location. */
export const PICKUP_LEAD_MINUTES = 60;

/** Auto-selected pick-up clocks stay on the time dropdown's 30-minute grid. */
export const PICKUP_TIME_GRID_MINUTES = 30;

/** The pick-up timezone lookup settles to a browser-zone fallback after this long. */
export const CAR_PICKUP_TIMEZONE_DEADLINE_MS = 5000;

/** Settled lookups kept in memory for the page; entries still in use are never evicted. */
export const CAR_PICKUP_TIMEZONE_CACHE_LIMIT = 50;

// Minimum rental period (2 days) to guarantee clearing providers' 24-hour minimum regardless of time of day.
export const MIN_RENTAL_DAYS = 2;

/** Expedia rejects pick-up/drop-off more than this many calendar days ahead. */
export const EXPEDIA_MAX_CAR_BOOKING_DAYS_AHEAD = 328;

export const EXPEDIA_SUPPORTED_CURRENCIES: ReadonlyArray<string> = [
  'USD',
  'CAD',
  'GBP',
  'EUR',
  'ARS',
  'AUD',
  'CHF',
  'DKK',
  'HKD',
  'JPY',
  'KRW',
  'NOK',
  'NZD',
  'SEK',
  'SGD',
  'TWD',
  'TRY',
];

export const EXPEDIA_SUPPORTED_LANGUAGES: ReadonlyArray<string> = [
  'ar-SA',
  'zh-CN',
  'zh-TW',
  'hr-HR',
  'cs-CZ',
  'da-DK',
  'nl-NL',
  'en-GB',
  'en-US',
  'fi-FI',
  'fr-FR',
  'fr-CA',
  'de-DE',
  'el-GR',
  'hu-HU',
  'is-IS',
  'id-ID',
  'it-IT',
  'ja-JP',
  'ko-KR',
  'lt-LT',
  'ms-MY',
  'nb-NO',
  'pl-PL',
  'pt-BR',
  'pt-PT',
  'ru-RU',
  'sk-SK',
  'es-MX',
  'es-ES',
  'sv-SE',
  'th-TH',
  'tr-TR',
  'uk-UA',
  'vi-VN',
  'zh-HK',
];

export const DEFAULT_REGION_BY_LANGUAGE: Readonly<Record<string, string>> = {
  ar: 'ar-SA',
  zh: 'zh-CN',
  hr: 'hr-HR',
  cs: 'cs-CZ',
  da: 'da-DK',
  nl: 'nl-NL',
  en: 'en-US',
  fi: 'fi-FI',
  fr: 'fr-FR',
  de: 'de-DE',
  el: 'el-GR',
  hu: 'hu-HU',
  is: 'is-IS',
  id: 'id-ID',
  it: 'it-IT',
  ja: 'ja-JP',
  ko: 'ko-KR',
  lt: 'lt-LT',
  ms: 'ms-MY',
  nb: 'nb-NO',
  pl: 'pl-PL',
  pt: 'pt-PT',
  ru: 'ru-RU',
  sk: 'sk-SK',
  es: 'es-ES',
  sv: 'sv-SE',
  th: 'th-TH',
  tr: 'tr-TR',
  uk: 'uk-UA',
  vi: 'vi-VN',
};

export const CAR_QUERY_PARAM_KEYS = {
  checkin: 'checkin',
  checkout: 'checkout',
  pickupLat: 'pickupLat',
  pickupLng: 'pickupLng',
  driverAge: 'driverAge',
  dropoffLat: 'dropoffLat',
  dropoffLng: 'dropoffLng',
  pickupTime: 'pickupTime',
  pickupCity: 'pickupCity',
  dropoffTime: 'dropoffTime',
  dropoffCity: 'dropoffCity',
  pickupPlaceId: 'pickupPlaceId',
  pickupAddress: 'pickupAddress',
  dropoffPlaceId: 'dropoffPlaceId',
  dropoffAddress: 'dropoffAddress',
  packagedProduct: 'packagedProduct',
  pickupDestination: 'pickupDestination',
  dropoffDestination: 'dropoffDestination',
} as const;

export const DRIVER_AGE_RANGE = {
  MIN: 18,
  MAX: 99,
} as const;

/** Matches the car-name filter's debounce, so a typed or stepped age settles before it runs a search. */
export const DRIVER_AGE_COMMIT_DEBOUNCE_MS = 700;

export const HOTEL_LOCATION_QUERY_PARAM_KEYS = {
  lat: 'lat',
  lng: 'lng',
  destination: 'destination',
  googlePlaceId: 'googlePlaceId',
} as const;

export const CAR_PAYMENT_OPTION_ORDER: ReadonlyArray<string> = [CarPaymentCollection.PAY_NOW, CarPaymentCollection.PAY_AT_PICKUP];

export const DEFAULT_CAR_FILTERS: CarFilters = {
  carTypes: [],
  companies: [],
  fuelTypes: [],
  driveTypes: [],
  paymentOptions: [],
  passengerBuckets: [],
  transmissionTypes: [],
  cancellationTypes: [],
  airportPickupOnly: false,
  airConditionedOnly: false,
  priceRange: [CAR_PRICE_RANGE.MIN, CAR_PRICE_RANGE.MAX],
};

export const CAR_RATES_SORT_OPTIONS: ReadonlyArray<CarRatesSortDto> = ['recommended', 'total_price', 'distance', 'customer_rating'];

export const DEFAULT_CAR_SORT_OPTION: CarRatesSortDto = 'recommended';

export const PASSENGER_BUCKETS: ReadonlyArray<number> = [2, 4, 5, 7];
